import { Inject } from '@odinjs/odin';

import { Endpoint, IeUndeterminedValidity, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { CheckboxAPI, FetchAPI, FieldAPI, FileUploaderAPI, FormAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';

import { ProviderAccreditationDocumentDetailAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { Document } from './provider-accreditation-documents-controller';

enum DocumentDateStatus {
  OPTIONAL = 'S',
  DO_NOT_REQUEST = 'N',
  MANDATORY = 'O',
  MANDATORIY_AND_UNDEFINED = 'I'
}

@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION_DOCUMENT_DETAIL })
class ProviderAccreditationDocumentDetailController extends BaseController {

  @Inject(Aliases.FIELD_EXPIRATION_DATE)
  private fieldExpiratonDate: FieldAPI;
  @Inject(Aliases.FIELD_EXPIRATION_DATE_DISABLED)
  private fieldExpiratonDateDisabled: FieldAPI;
  @Inject(Aliases.FORM_DOCUMENT_DETAIL)
  private formDocumentDetail: FormAPI;
  @Inject(Aliases.FIELD_ISSUE_DATE_DISABLED)
  private fieldIssueDateDisabled: FieldAPI;
  @Inject(Aliases.FIELD_ISSUE_DATE)
  private fieldIssueDate: FieldAPI;
  @Inject(Aliases.CHECKBOX_UNDETERMINED_VALIDITY)
  private checkboxUndeterminedValidity: CheckboxAPI;

  @OnMount(Aliases.FETCH_DOCUMENT_DETAIL)
  onMountFetchDocumentDetail(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqUser, nrSeqAccreditation, nrSeqTypeDocument } = this.context.getBulkData() as Record<string, string>;
    api.setParameters({ nrSeqUser, nrSeqAccreditation, nrSeqTypeDocument });
    api.forceFetch();
  }

  @OnMount(Aliases.FILE_UPLOADER)
  onMountFileUploader(event: MountEvent): void {
    const { dsDocument, nrSeqTypeDocument } = this.context.getBulkData();
    const predefinedDocuments = {
      key: nrSeqTypeDocument as string,
      value: dsDocument as string,
    };
    const api = event.target as FileUploaderAPI;
    api.setPredefinedFiles([predefinedDocuments]);
  }

  @OnAfterFetch(Aliases.FETCH_DOCUMENT_DETAIL)
  onAfterFetchDocumentDetail(event: FetchEvent): void {
    if (!event.data) return;
    const data = event.data;
    const { ieIssueDt, ieExpirationDt, ieStatusCheck } = data as Document;
    switch (ieIssueDt) {
      case DocumentDateStatus.DO_NOT_REQUEST:
        this.fieldIssueDate.setVisible(false);
        this.fieldIssueDateDisabled.setVisible(true);
        break;
      case DocumentDateStatus.OPTIONAL:
        this.fieldIssueDate.setVisible(true);
        this.fieldIssueDateDisabled.setVisible(false);
        break;
      case DocumentDateStatus.MANDATORY:
        this.fieldIssueDate.setVisible(true);
        this.fieldIssueDateDisabled.setVisible(false);
        this.fieldIssueDate.setValidators([{ name: 'required', options: {} }, { name: 'before_current_date' }] as Validator[]);
        break;
    }
    switch (ieExpirationDt) {
      case DocumentDateStatus.DO_NOT_REQUEST:
        this.fieldExpiratonDate.setVisible(false);
        this.fieldExpiratonDateDisabled.setVisible(true);
        this.checkboxUndeterminedValidity.setEnabled(false);
        break;
      case DocumentDateStatus.OPTIONAL:
        this.fieldExpiratonDate.setVisible(true);
        this.fieldExpiratonDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(false);
        break;
      case DocumentDateStatus.MANDATORY:
        this.fieldExpiratonDate.setVisible(true);
        this.fieldExpiratonDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(false);
        this.fieldExpiratonDate.setValidators([{ name: 'required', options: {} }, { name: 'after_current_date' }] as Validator[]);
        break;
      case DocumentDateStatus.MANDATORIY_AND_UNDEFINED:
        this.fieldExpiratonDate.setVisible(true);
        this.fieldExpiratonDateDisabled.setVisible(false);
        this.checkboxUndeterminedValidity.setEnabled(true);
        this.fieldExpiratonDate.setValidators([{ name: 'required', options: {} }, { name: 'after_current_date' }] as Validator[]);
        break;
    }

    if (ieStatusCheck ) {
      this.fieldExpiratonDate.setVisible(false);
      this.fieldExpiratonDateDisabled.setVisible(true);
    }
  }
  @OnChange(Aliases.FIELD_UNDETERMINED_VALIDITY)
  onChangeFieldUndeterminedValidity(event: ChangeEvent<Document>): void {
    const checked = event.value;
    if (!checked) {
      this.fieldExpiratonDate.setValidators([{ name: 'required', options: {} }, { name: 'after_current_date' }] as Validator[]);
      this.fieldExpiratonDate.setVisible(true);
      this.fieldExpiratonDateDisabled.setVisible(false);
    } else {
      this.fieldExpiratonDate.setData({});
      this.fieldExpiratonDate.setValue('');
      this.fieldExpiratonDate.removeValidator('required');
      this.fieldExpiratonDate.setVisible(false);
      this.fieldExpiratonDateDisabled.setVisible(true);
    }
  }

  @OnSubmit(Aliases.FORM_DOCUMENT_DETAIL)
  onSubmitFormDocumentDetail(event: SubmitEvent): void {
    this.formDocumentDetail.setLoading(true);
    const data = event.record;
    const { nrSeqTypeDocument } = this.context.getBulkData();
    const { nrSeqUser, nrSeqAccreditation } = HistoryService.getCurrentRouteParams();
    const listAttachments = data.attachments;
    let newAttachment = {};
    if (Array.isArray(listAttachments)) {
      listAttachments.forEach((element) => {
        newAttachment = {
          filename: element.filename,
          filepath: element.filepath,
          size: element.size,
        };
        return newAttachment;
      });
    }
    const { ieStatusCheck } = data as Document;
    data.ieUndeterminedValidity = ieStatusCheck ? IeUndeterminedValidity.MARKED : IeUndeterminedValidity.UNMARKED;
    const body = { ...data, newAttachment, nrSeqAccreditation };
    HttpService.post(Endpoint.POST.SEND_DOCUMENT, { nrSeqUser, nrSeqAccreditation, nrSeqTypeDocument }, { body })
      .then(() => {
        ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-success'));
      })
      .catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
      })
      .finally(() => {
        ModalService.close('provider-accreditation-document-detail');
      });
  }

  @OnCancel(Aliases.FORM_DOCUMENT_DETAIL)
  onCancelFormDocumentDetail(): void {
    ModalService.close('provider-accreditation-document-detail');
  }
}

export {
  ProviderAccreditationDocumentDetailController,
};
